/**
 * Product vocabulary for staff surfaces (GDN-3226, PRD §3 and AC-3).
 *
 * The words staff read are Missing, Requested, Received, Waived and Remind.
 * Only four of those are storage states. "Requested" is DERIVED from delivery
 * state and is never a completion status — a delivered message says nothing
 * about whether the patient acted, so it must never be mistaken for Received.
 */

import type {
  DeliveryHistoryResponse,
  DeliveryTouchpoint,
  RequirementRow,
} from './types';

/** What a staff-facing row reads as. */
export type DisplayState = 'missing' | 'requested' | 'received' | 'waived' | 'cancelled';

/**
 * Map a row to its staff-facing state.
 *
 * `display_state` from the backend carries the settled states; "Requested" is
 * layered on top of Missing when an active outreach session exists. The backend
 * deliberately does not encode it, because it belongs to the message lifecycle
 * rather than the requirement lifecycle.
 */
export const resolveDisplayState = (
  row: Pick<RequirementRow, 'display_state' | 'state'>,
  hasActiveSession: boolean,
): DisplayState => {
  // Prefer the storage state: display_state is a presentation string, and a
  // backend that adds a value we do not know about should still land somewhere
  // sensible rather than silently reading as Missing.
  switch (row.state) {
    case 'satisfied':
      return 'received';
    case 'waived':
      return 'waived';
    case 'cancelled':
      return 'cancelled';
    case 'open':
      return hasActiveSession ? 'requested' : 'missing';
    default:
      return row.display_state?.toLowerCase() === 'received' ? 'received' : 'missing';
  }
};

export const DISPLAY_STATE_LABELS: Record<DisplayState, string> = {
  missing: 'Missing',
  requested: 'Requested',
  received: 'Received',
  waived: 'Waived',
  cancelled: 'Cancelled',
};

/** A row still owes the practice something. Drives the missing-item count (AC-3). */
export const isOutstanding = (state: DisplayState): boolean =>
  state === 'missing' || state === 'requested';

/**
 * AC-4: the action reads "Send request" until a request exists, then "Remind".
 * The backend states the rule directly on the delivery history.
 */
export const sendActionLabel = (hasActiveSession: boolean): 'Send request' | 'Remind' =>
  hasActiveSession ? 'Remind' : 'Send request';

/**
 * When a touchpoint reached the patient, for ordering and for the "requested by
 * SMS 4 days ago" line. Touchpoints carry four timestamps and any of them can be
 * null; prefer the one that means "the patient could have seen it".
 *
 * Deliberately NOT the attempts array: an attempt records an outbox try
 * (channel, sequence, status) and carries no send time, only `scheduled_at`.
 */
export const touchpointTimestamp = (
  touchpoint: DeliveryTouchpoint,
): string | undefined =>
  touchpoint.delivered_at ?? touchpoint.sent_at ?? touchpoint.queued_at ?? undefined;

/**
 * Most recent touchpoint, newest first — the derived half of AC-3. Returns
 * undefined when nothing has gone out, which is what makes a row read Missing
 * rather than Requested.
 */
export const latestTouchpoint = (
  history: DeliveryHistoryResponse | undefined,
): DeliveryTouchpoint | undefined => {
  const touchpoints = history?.touchpoints ?? [];
  if (touchpoints.length === 0) return undefined;
  return [...touchpoints].sort((a, b) =>
    (touchpointTimestamp(b) ?? '').localeCompare(touchpointTimestamp(a) ?? ''),
  )[0];
};
